import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Moon, Sun, Globe, Menu, X, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/brand/Logo";
import { scrollToTarget } from "@/lib/motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const langLabel = { ar: "العربية", en: "English", tr: "Türkçe", "zh-CN": "简体中文" };

export default function Header() {
  const { theme, toggle } = useTheme();
  const { lang, t, changeLang, supported } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const isHome = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  const solid = scrolled || !isHome || mobileOpen;
  /* Over the dark hero the header is light-on-dark; once solid it follows the theme. */
  const overDark = solid ? theme === "dark" : true;
  const logoTone = overDark ? "dark" : "light";

  const goTrack = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (isHome) scrollToTarget("#track");
    else navigate("/track");
  };

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/track", label: t.nav.track, onClick: goTrack },
    { to: "/contact", label: t.nav.contact },
  ];

  const tone = {
    idle: overDark ? "text-[var(--on-dark-muted)] hover:text-[var(--on-dark)]" : "text-[var(--muted-ink)] hover:text-[var(--ink)]",
    active: overDark ? "text-[var(--signal)]" : "text-[var(--accent-ink)]",
    bar: overDark ? "bg-[var(--signal)]" : "bg-[var(--accent-ink)]",
    btn: overDark ? "text-[var(--on-dark-muted)] hover:text-[var(--on-dark)] hover:bg-white/10" : "text-[var(--muted-ink)] hover:text-[var(--ink)]",
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-header transition-[background-color,border-color] duration-300 ease-out-quart border-b ${
        solid
          ? "bg-[color-mix(in_oklch,var(--bg)_86%,transparent)] backdrop-blur-xl border-[var(--line)]"
          : "bg-transparent border-transparent"
      } ${overDark ? "on-dark" : ""}`}
    >
      <div className="container-x flex h-16 lg:h-[72px] items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="mk-logo-link flex items-center shrink-0" dir="ltr" aria-label="Misk Cargo home">
          <Logo tone={logoTone} className="h-9 lg:h-10 w-auto" title="Misk Cargo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={n.onClick}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 text-[15px] font-semibold transition-colors ${isActive ? tone.active : tone.idle}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    aria-hidden
                    className={`absolute inset-x-4 -bottom-0.5 h-[2px] origin-center transition-transform duration-500 ease-out-expo ${tone.bar} ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="lang-switch" className={`h-10 gap-1.5 ${tone.btn}`} aria-label="Change language">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline text-xs font-semibold">{langLabel[lang]}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-36 z-dropdown">
              {supported.map((code) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`lang-option-${code}`}
                  onClick={() => changeLang(code)}
                  className={lang === code ? "font-bold" : ""}
                >
                  {langLabel[code]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            data-testid="theme-toggle"
            onClick={toggle}
            className={`h-10 w-10 ${tone.btn}`}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          <Link to="/contact" className="hidden md:block">
            <Button data-testid="header-cta" className="btn-amber h-10 px-5 rounded-full border-0">
              {t.nav.getQuote}
            </Button>
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg ${tone.btn}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-menu" className="lg:hidden border-t border-[var(--line)] bg-[var(--bg)] text-[var(--ink)]" data-testid="mobile-menu">
          <nav className="container-x py-4 flex flex-col gap-1" aria-label="Mobile">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={n.onClick}
                data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-4 py-3.5 rounded-xl text-lg font-semibold ${
                    isActive ? "bg-[var(--surface)] text-[var(--accent-ink)]" : "hover:bg-[var(--surface)]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-cta">
              <Button className="btn-amber w-full mt-2 h-12 rounded-xl border-0">{t.nav.getQuote}</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
